import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import T from 'prop-types';

import fetchWrapper from 'utils/fetch';
import { locationDataProp, formFriendlyData } from './locationData';
import { migrateFormValidator, mapAccountErrors } from './utils';
import { ErrorMessage, Label, Field, Select } from './FormElements';

function MigrateForm(props) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const countries = formFriendlyData(props.locationData.countries);
  const regions = formFriendlyData(props.locationData.usStates);
  const defaultCountry = countries.find((country) => country.value === props.countryCode) || { label: '', value: '' };
  const defaultRegion = regions.find((region) => region.value === props.region) || { label: '', value: '' };

  const initialFormValues = {
    billingEmail: props.billingEmail || '',
    country: defaultCountry,
    addressLineOne: props.addressLineOne || '',
    addressLineTwo: props.addressLineTwo || '',
    city: props.city || '',
    postCode: props.postCode || '',
    state: defaultRegion,
    companyName: props.companyName || '',
    vatNumber: props.vatNumber || '',
    packageId: props.packageId || undefined,
  };

  const { editAccountLink } = props;

  const onSubmit = async (values, { setErrors }) => {
    setIsSubmitting(true);

    const data = {
      payg_migration: {
        company_name: values.companyName,
        billing_email: values.billingEmail,
        address_1: values.addressLineOne,
        address_2: values.addressLineTwo,
        city: values.city,
        region: values.state?.value,
        country_code: values.country?.value,
        postal_code: values.postCode,
        vat_number: values.vatNumber,
      },
      package: {
        id: values.packageId,
      },
    };

    try {
      const response = await fetchWrapper('/api/pricing/payg_migration', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      const result = await response.json();

      if (response.status === 201) {
        const location = response.headers.get('location');
        window.location.assign(location);
      }

      const errors = {
        headerErrors: result.errors.header_errors,
        ...result.errors.user,
        ...mapAccountErrors(result.errors.account),
      };

      setErrors(errors);
      setIsSubmitting(false);
    } catch (errors) {
      setErrors(errors);
      setIsSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialFormValues}
      validateOnChange={false}
      validateOnBlur={false}
      validate={migrateFormValidator}
      onSubmit={onSubmit}
    >
      {({ setFieldValue, values, errors }) => (
        <Form className="flex flex-col gap-8 mb-10">
          <div>
            <h3 className="text-h3 font-medium pb-2">Billing details</h3>
            <p className="text-p3 font-medium text-dark-grey">
              Provide your business and tax information, we&lsquo;ll use the payment details on record for your account.
              View{' '}
              <a href={editAccountLink} target="_blank" rel="noreferrer">
                your package details
              </a>{' '}
              to update your payment details.
            </p>
          </div>

          <div>
            <Label htmlFor="companyName">Company name</Label>
            <Field id="companyName" name="companyName" placeholder="Optional company name" />
            <p className="ui-text-p3 text-neutral-500 mt-1">
              An optional company name to be used for billing purposes. If left blank, your name will be used.
            </p>
          </div>

          <div>
            <Label htmlFor="billingEmail">Billing email</Label>
            <Field
              id="billingEmail"
              name="billingEmail"
              placeholder="Alternative billing email address"
              error={errors.billingEmail}
            />
            <ErrorMessage name="billingEmail" />
            <p className="ui-text-p3 text-neutral-500 mt-1">
              Invoices and billing notifications are sent to the account owner&apos;s email. Provide an alternative
              address here to send them elsewhere.
            </p>
          </div>

          <div>
            <Label>Billing address</Label>
            <div className="flex flex-col gap-2">
              <Select
                id="country"
                name="country"
                placeholder="Country"
                options={countries}
                onChange={(option) => setFieldValue('country', option)}
                defaultValue={defaultCountry.value ? defaultCountry : null}
              />
              <ErrorMessage name="country" />
              <Field name="addressLineOne" placeholder="Address" error={errors.addressLineOne} />
              <ErrorMessage name="addressLineOne" />
              <Field name="addressLineTwo" placeholder="Address line 2 (optional)" />
              <div className="flex gap-2 max-w-screen-sm">
                <div className="w-full">
                  <Field name="city" placeholder="City" error={errors.city} />
                  <ErrorMessage name="city" />
                </div>
                <div className="w-full">
                  <Field
                    name="postCode"
                    placeholder={values.country.value === 'US' ? 'Zip' : 'Post Code'}
                    error={errors.postCode}
                  />
                  <ErrorMessage name="postCode" />
                </div>
              </div>
              {values.country.value === 'US' ? (
                <>
                  <Select
                    name="state"
                    placeholder="State"
                    options={regions}
                    onChange={(option) => setFieldValue('state', option)}
                    defaultValue={defaultRegion.value ? defaultRegion : null}
                  />
                  <ErrorMessage name="state" />
                </>
              ) : null}
            </div>
          </div>

          <div>
            <Label htmlFor="vatNumber">VAT number</Label>
            <Field id="vatNumber" name="vatNumber" placeholder="GB123456789" />
          </div>

          <div>
            <Field type="hidden" name="packageId" id="packageId" />
            <ErrorMessage name="packageId" />
          </div>

          <button id="confirmPurchaseBtn" type="submit" className="flex ui-btn self-end" disabled={isSubmitting}>
            {isSubmitting ? 'Submitting.....' : 'Confirm migration'}
          </button>
        </Form>
      )}
    </Formik>
  );
}

MigrateForm.propTypes = {
  clientSecret: T.string,
  billingEmail: T.string,
  addressLineOne: T.string,
  addressLineTwo: T.string,
  city: T.string,
  postCode: T.string,
  companyName: T.string,
  vatNumber: T.string,
  region: T.string,
  countryCode: T.string,
  editAccountLink: T.string,
  locationData: locationDataProp,
  packageId: T.string.isRequired,
};

export { MigrateForm };
